/*
 * DIALOG SHELL
 *
 * Provides the accessible overlay, panel, header, body, footer, and close behavior for dialogs.
 * Recent changes:
 * - Adds optional direct title and close-control styling hooks.
 * - Constrains long dialog bodies to an internal scroll area at small viewport heights.
 * - Supports an unpadded body for feature layouts that provide their own edge-to-edge spacing.
 */

import { ReactNode, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { foundationRadiusTokens, foundationSemantic, foundationSpacingTokens } from '../../foundation';
import Button from './Button';
import { cx } from './utils';

interface DialogShellProps {
  open: boolean;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  className?: string;
  panelClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  closeButtonClassName?: string;
  bodyClassName?: string;
  bodyPadding?: boolean;
  footerClassName?: string;
}

export default function DialogShell({
  open,
  title,
  children,
  footer,
  onClose,
  className,
  panelClassName,
  headerClassName,
  titleClassName,
  closeButtonClassName,
  bodyClassName,
  bodyPadding = true,
  footerClassName
}: DialogShellProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open || !onClose) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const dialogContent = (
    <div
      className={cx('fixed inset-0 z-50', className)}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      <div className={cx('fixed inset-0', foundationSemantic.dialog.overlay)} onClick={onClose} />
      <div
        className={cx(
          'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          foundationRadiusTokens.lg,
          'flex w-[90vw] max-w-[800px] max-h-[90vh] flex-col overflow-hidden',
          foundationSemantic.dialog.panel,
          panelClassName
        )}
      >
        {title && (
          <div className={cx(`flex items-center justify-between ${foundationSpacingTokens.dialog.header}`, foundationSemantic.dialog.header, headerClassName)}>
            <h3 id={titleId} className={cx(foundationSemantic.dialog.title, titleClassName)}>{title}</h3>
            {onClose && (
              <Button variant="icon" aria-label="Close dialog" className={closeButtonClassName} onClick={onClose}>
                ×
              </Button>
            )}
          </div>
        )}
        <div className={cx(bodyPadding && foundationSpacingTokens.dialog.body, 'min-h-0 flex-1 overflow-auto', bodyClassName)}>{children}</div>
        {footer && <div className={cx(foundationSpacingTokens.dialog.footer, foundationSemantic.dialog.footer, footerClassName)}>{footer}</div>}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return dialogContent;
  }

  return createPortal(dialogContent, document.body);
}

export type { DialogShellProps };
